import { useState, useEffect, useMemo } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import {
  Paper,
  Stack,
  Text,
  Button,
  Group,
  SegmentedControl,
  Checkbox,
  TextInput,
  SimpleGrid,
  Select,
  Radio,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'

import { INVITAE_QUESTIONNAIRE_ID, createInitialFormState, TEST_PANELS } from './formData'
import type { InvitaeFormState } from './formData'
import {
  INVITAE_CARDIOLOGY_QUESTIONNAIRE_ID,
  createEmptyCardiacHistory,
  CARDIOLOGY_TEST_PANELS,
} from './cardiologyFormData'
import { CancerSections } from './CancerSections'
import { CardiologySections } from './CardiologySections'
import { useAutoPopulate } from './shared/useAutoPopulate'
import { useSignature } from './shared/useSignature'
import { useCanOrder } from './shared/useCanOrder'
import { useOrderSubmission } from './shared/useOrderSubmission'
import { FormHeader } from './shared/FormHeader'
import { SignatureSection } from './shared/SignatureSection'
import { generateBeHumanOrderPdf } from './pdf/behumanPdfGenerator'
import { mapInvitaeCancerToPdf } from './pdf/mapInvitaeCancerToPdf'
import { mapInvitaeCardiologyToPdf } from './pdf/mapInvitaeCardiologyToPdf'
import { validateDate as validateSpecimenDate } from './shared/formValidators'
import type { AutoPopulateData } from './pdf/pdfTypes'
import { OrderFormLayout } from './shared/OrderFormLayout'
import { OrderCart } from './shared/OrderCart'
import type { CartItem } from './shared/OrderCart'
import { OrderReviewSummary } from './shared/OrderReviewSummary'
import type { ReviewSection } from './shared/OrderReviewSummary'
import { SIGNATURE_REQUIRED_TOOLTIP } from './shared/constants'
import { TooltipButton } from './shared/TooltipButton'
import { buildInvitaeHistorySections } from './invitaeReviewSections'

type Props = {
  patient: Patient
  onBack: () => void
}

export const InvitaeRequisitionForm = ({ patient, onBack }: Props) => {
  const {
    patientDefaults,
    providerDefaults,
    insuranceDefaults,
    diagnosisDefaults,
    orgError,
    insuranceWarning,
  } = useAutoPopulate(patient)
  const signature = useSignature()
  const { canOrder: isPractitioner, loading: canOrderLoading } = useCanOrder()

  const autoPopulateData: AutoPopulateData = {
    patient: patientDefaults,
    provider: providerDefaults,
    insurance: insuranceDefaults,
  }

  const form = useForm<InvitaeFormState>({
    initialValues: createInitialFormState(),
    validateInputOnBlur: true,
    validate: {
      specimenCollectionDate: validateSpecimenDate,
    },
  })

  useEffect(() => {
    if (providerDefaults.organizationName) {
      form.setValues({
        organizationName: providerDefaults.organizationName,
        orgPhone: providerDefaults.orgPhone,
        orgFax: providerDefaults.orgFax,
        orgAddress: providerDefaults.orgAddress,
        orgCity: providerDefaults.orgCity,
        orgState: providerDefaults.orgState,
        orgZip: providerDefaults.orgZip,
        orgCountry: providerDefaults.orgCountry,
      })
    }
  }, [providerDefaults.organizationName]) // eslint-disable-line react-hooks/exhaustive-deps

  // Default the kit shipping address to the patient's address (editable)
  useEffect(() => {
    if (patientDefaults.address && !form.values.kitShippingAddress) {
      form.setValues({
        kitShippingAddress: patientDefaults.address,
        kitShippingCityStateZip: patientDefaults.cityStateZip,
      })
    }
  }, [patientDefaults.address]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-apply saved signature
  useEffect(() => {
    if (signature.savedSignature && !form.values.signatureDataUrl) {
      form.setFieldValue('signatureDataUrl', signature.savedSignature)
      form.setFieldValue(
        'signatureDate',
        new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }),
      )
    }
  }, [signature.savedSignature]) // eslint-disable-line react-hooks/exhaustive-deps

  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(
    new Set(['STAT Turnaround Time']),
  )

  const questionnaireId =
    form.values.formType === 'cardiology'
      ? INVITAE_CARDIOLOGY_QUESTIONNAIRE_ID
      : INVITAE_QUESTIONNAIRE_ID

  const submission = useOrderSubmission({
    patientId: patient.id,
    questionnaireId,
    generatePdfBytes: () => {
      const pdfData =
        form.values.formType === 'cardiology'
          ? mapInvitaeCardiologyToPdf(form.values, autoPopulateData)
          : mapInvitaeCancerToPdf(form.values, autoPopulateData)
      return generateBeHumanOrderPdf(pdfData)
    },
    buildFileName: () => {
      const formLabel = form.values.formType === 'cardiology' ? 'Invitae_Cardio' : 'Invitae_Order'
      return `${formLabel}_${patientDefaults.lastName}_${patientDefaults.firstName}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`
    },
    getFormData: () => form.values as unknown as Record<string, unknown>,
    vendorName: form.values.formType === 'cardiology' ? 'Invitae Cardiology' : 'Invitae Cancer',
    laboratoryOrganizationName: 'Invitae',
    diagnosisDefaults,
    getDiagnosisText: () => form.values.diagnosisText,
    orgError,
  })

  const handleFormTypeChange = (newType: 'cancer' | 'cardiology') => {
    form.setValues({
      formType: newType,
      selectedTests: [],
      individualGenes: '',
      primaryIndication: '',
      primaryIndicationOther: '',
      // Reset diagnosis codes on type switch so cancer-context codes don't carry
      // into a cardiology requisition; the seeding hook re-fills from active dx.
      diagnosisText: '',
      specimenType: '',
      dnaSource: '',
      specimenCollectionDate: '',
      specimenId: '',
      ...(newType === 'cardiology'
        ? {
            cardiacHistory: createEmptyCardiacHistory(),
            noPersonalHistory: false,
            cancerHistory: [],
          }
        : { cardiacHistory: null, requestedVariants: '' }),
    })
    form.clearErrors()
    setExpandedPanels(
      new Set(newType === 'cancer' ? ['STAT Turnaround Time'] : ['Arrhythmia and Cardiomyopathy']),
    )
  }

  const togglePanel = (category: string) => {
    setExpandedPanels((prev) => {
      const next = new Set(prev)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })
  }

  const handleSignatureConfirm = (dataUrl: string, date: string) => {
    form.setFieldValue('signatureDataUrl', dataUrl)
    form.setFieldValue('signatureDate', date)
    signature.closeSignatureModal()
  }

  const validateAges = (): boolean => {
    let valid = true
    for (const entry of form.values.cancerHistory) {
      if (entry.ageOfDiagnosis !== '') {
        const num = Number(entry.ageOfDiagnosis)
        if (isNaN(num) || num < 0 || num > 200 || !Number.isInteger(num)) {
          form.setFieldError(`age_${entry.type}`, 'Age must be 0-200')
          valid = false
        }
      }
    }
    return valid
  }

  const hasSelectedTests = () =>
    form.values.selectedTests.length > 0 || form.values.individualGenes.trim().length > 0

  const handleSubmitOrder = async () => {
    if (!hasSelectedTests()) {
      notifications.show({
        title: 'No Tests Selected',
        message: 'Please select at least one test before submitting.',
        color: 'red',
      })
      return
    }
    if (!form.values.signatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the form before submitting.',
        color: 'red',
      })
      return
    }

    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please correct the highlighted fields before submitting.',
        color: 'red',
      })
      return
    }

    if (form.values.formType === 'cancer' && !validateAges()) {
      notifications.show({
        title: 'Invalid Age',
        message: 'Please correct the age of diagnosis fields (must be 0-200).',
        color: 'red',
      })
      return
    }

    await submission.handleSubmitOrder()
  }

  const [showReview, setShowReview] = useState(false)

  const invitaeTestLookup = useMemo(() => {
    const map = new Map<string, { name: string; category: string }>()
    for (const panel of [...TEST_PANELS, ...CARDIOLOGY_TEST_PANELS]) {
      for (const test of panel.tests) {
        map.set(test.code, { name: test.name, category: panel.category })
      }
    }
    return map
  }, [])

  const reviewTests: CartItem[] = useMemo(() => {
    const tests: CartItem[] = []
    for (const code of form.values.selectedTests) {
      const def = invitaeTestLookup.get(code)
      tests.push({
        id: code,
        code,
        name: def?.name ?? code,
        category: def?.category ?? 'Tests',
      })
    }
    if (form.values.individualGenes.trim()) {
      tests.push({
        id: 'individual',
        code: 'individual',
        name: form.values.individualGenes,
        category: 'Individual Genes',
      })
    }
    return tests
  }, [form.values.selectedTests, form.values.individualGenes, invitaeTestLookup])

  const handleCartRemove = (id: string) => {
    if (id === 'individual') {
      form.setFieldValue('individualGenes', '')
    } else {
      form.setFieldValue(
        'selectedTests',
        form.values.selectedTests.filter((code) => code !== id),
      )
    }
  }

  const reviewSections: ReviewSection[] = useMemo(() => {
    const sections: ReviewSection[] = []
    sections.push({
      title: 'Form Type',
      items: [
        {
          label: 'Type',
          value: form.values.formType === 'cardiology' ? 'Cardiology' : 'Hereditary Cancer',
        },
      ],
    })
    const specimen = [
      { label: 'Specimen Type', value: form.values.specimenType },
      { label: 'DNA Source', value: form.values.dnaSource },
      { label: 'Collection Date', value: form.values.specimenCollectionDate },
      { label: 'Specimen ID', value: form.values.specimenId },
    ]
    if (specimen.some((i) => i.value)) {
      sections.push({ title: 'Specimen', items: specimen })
    }
    if (form.values.primaryIndication) {
      sections.push({
        title: 'Clinical Indication',
        items: [
          { label: 'Primary Indication', value: form.values.primaryIndication },
          { label: 'Other', value: form.values.primaryIndicationOther },
        ],
      })
    }
    sections.push(...buildInvitaeHistorySections(form.values))
    return sections
  }, [form.values])

  const handleReviewOrder = () => {
    if (!hasSelectedTests()) {
      notifications.show({
        title: 'No Tests Selected',
        message: 'Please select at least one test before reviewing.',
        color: 'red',
      })
      return
    }
    if (!form.values.signatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the form before reviewing.',
        color: 'red',
      })
      return
    }
    const validation = form.validate()
    if (validation.hasErrors) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please correct the highlighted fields before reviewing.',
        color: 'red',
      })
      return
    }
    if (form.values.formType === 'cancer' && !validateAges()) {
      notifications.show({
        title: 'Invalid Age',
        message: 'Please correct the age of diagnosis fields (must be 0-200).',
        color: 'red',
      })
      return
    }
    setShowReview(true)
  }

  const formTypeTitle =
    form.values.formType === 'cardiology'
      ? 'Invitae Cardiology Requisition'
      : 'Invitae Hereditary Cancer Simplified Requisition'

  const formTypeLabel =
    form.values.formType === 'cardiology'
      ? 'TRF904-20 | Labcorp Genetics'
      : 'TRF903-28 | Labcorp Genetics'

  return (
    <OrderFormLayout
      cart={<OrderCart items={reviewTests} onRemove={handleCartRemove} vendorLabel="Invitae" />}
      header={
        <FormHeader
          title={formTypeTitle}
          subtitle={formTypeLabel}
          onBack={showReview ? () => setShowReview(false) : onBack}
          onPreview={submission.handlePreviewPdf}
          onSubmit={handleSubmitOrder}
          exporting={submission.exporting}
          submitting={submission.submitting}
          orderSubmitted={submission.orderSubmitted}
          canSubmit={isPractitioner}
          canOrderLoading={canOrderLoading}
          hideActions
          submitTooltip="Practitioner permissions required to submit orders"
          labError={submission.labError}
          orgError={orgError}
          warnings={insuranceWarning ? [insuranceWarning] : []}
        />
      }
    >
      {showReview ? (
        <OrderReviewSummary
          vendorName={
            form.values.formType === 'cardiology' ? 'Invitae Cardiology' : 'Invitae Cancer'
          }
          tests={reviewTests}
          diagnosisText={form.values.diagnosisText}
          sections={reviewSections}
          signatureDataUrl={form.values.signatureDataUrl}
          signatureDate={form.values.signatureDate}
          onEdit={() => setShowReview(false)}
          onSubmit={handleSubmitOrder}
          onPreview={submission.handlePreviewPdf}
          submitting={submission.submitting}
          exporting={submission.exporting}
          orderSubmitted={submission.orderSubmitted}
          isPractitioner={isPractitioner}
          canOrderLoading={canOrderLoading}
          labError={submission.labError}
          orgError={orgError}
        />
      ) : (
        <Stack gap="lg">
          {/* ===== FORM TYPE SELECTOR ===== */}
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
              FORM TYPE
            </Text>
            <SegmentedControl
              value={form.values.formType}
              onChange={(v) => handleFormTypeChange(v as 'cancer' | 'cardiology')}
              data={[
                { label: 'Hereditary Cancer (TRF903-28)', value: 'cancer' },
                { label: 'Cardiology (TRF904-20)', value: 'cardiology' },
              ]}
              fullWidth
            />
          </Paper>

          {/* ===== FORM TYPE-SPECIFIC SECTIONS ===== */}
          {form.values.formType === 'cancer' ? (
            <CancerSections
              form={form}
              expandedPanels={expandedPanels}
              togglePanel={togglePanel}
              diagnosisDefaults={diagnosisDefaults}
            />
          ) : (
            <CardiologySections form={form} diagnosisDefaults={diagnosisDefaults} />
          )}

          {/* ===== KIT SHIPPING ===== */}
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
              KIT SHIPPING
            </Text>
            <Text size="xs" c="dimmed" mb="sm">
              Invitae will ship the collection kit for the selected test(s) to this address. Verify
              and update if needed.
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <TextInput label="Shipping Address" {...form.getInputProps('kitShippingAddress')} />
              <TextInput
                label="City, State, ZIP"
                {...form.getInputProps('kitShippingCityStateZip')}
              />
              <Select
                label="Phone Type"
                placeholder="Select..."
                data={[
                  { value: 'mobile', label: 'Mobile' },
                  { value: 'home', label: 'Home' },
                  { value: 'work', label: 'Work' },
                ]}
                {...form.getInputProps('kitShippingPhoneType')}
              />
            </SimpleGrid>
            <Radio.Group
              label="Language Preference"
              mt="sm"
              {...form.getInputProps('kitShippingLanguagePreference')}
            >
              <Group mt={4}>
                <Radio value="english" label="English" />
                <Radio value="spanish" label="Spanish" />
                <Radio value="other" label="Other" />
              </Group>
            </Radio.Group>
          </Paper>

          {/* ===== SIGNATURE ===== */}
          <SignatureSection
            signatureDataUrl={form.values.signatureDataUrl}
            signatureDate={form.values.signatureDate}
            savedSignature={signature.savedSignature}
            signatureModalOpen={signature.signatureModalOpen}
            onOpenModal={signature.openSignatureModal}
            onCloseModal={signature.closeSignatureModal}
            onConfirm={handleSignatureConfirm}
            onSaveSignature={signature.handleSaveSignature}
            certificationText="By submitting this form, I acknowledge that the patient has been supplied information regarding and consented to undergo genetic testing, as set forth in Labcorp Genetics' Informed Consent for Genetic Testing."
          />

          {/* ===== ACTIONS ===== */}
          <Group justify="flex-end" pb="xl">
            <Checkbox label="Dispatch Phlebotomist" disabled />
            <Button variant="default" onClick={onBack}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={submission.handlePreviewPdf}
              loading={submission.exporting}
            >
              Download PDF
            </Button>
            <TooltipButton
              color="teal"
              disabled={submission.orderSubmitted || !form.values.signatureDataUrl}
              disabledReason={!form.values.signatureDataUrl ? SIGNATURE_REQUIRED_TOOLTIP : null}
              onClick={handleReviewOrder}
            >
              {submission.orderSubmitted ? 'Order Submitted' : 'Review Order'}
            </TooltipButton>
          </Group>
        </Stack>
      )}
    </OrderFormLayout>
  )
}
